// Thresholds — per-stage rot threshold configuration.
//
// Mirrors server-side validation: 1–365 days, allowed stages only.
// PUT /api/v1/thresholds upserts one stage at a time.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { Settings, Check, AlertCircle } from 'lucide-react';

import { fetchThresholds, upsertThreshold } from '../api';

const STAGES = ['Prospect', 'Discovery', 'Demo', 'Proposal', 'Closing'] as const;
type Stage = (typeof STAGES)[number];

// Defaults per spec §4: Prospect=7, Discovery=14, Demo=21, Proposal=30, Closing=14.
const DEFAULTS: Record<Stage, number> = {
  Prospect: 7,
  Discovery: 14,
  Demo: 21,
  Proposal: 30,
  Closing: 14,
};

const DAY_MIN = 1;
const DAY_MAX = 365;

function validateDays(v: number): string | null {
  if (!Number.isInteger(v)) return 'Must be a whole number';
  if (v < DAY_MIN) return `Minimum is ${DAY_MIN} day`;
  if (v > DAY_MAX) return `Maximum is ${DAY_MAX} days`;
  return null;
}

export interface ThresholdsProps {
  integrationID: string;
}

export function Thresholds({ integrationID }: ThresholdsProps) {
  const qc = useQueryClient();

  const thresholds = useQuery({
    queryKey: ['pipeline-rot', 'thresholds'],
    queryFn: fetchThresholds,
  });

  // Local state: days per stage (seeded from API; falls back to defaults).
  const [values, setValues] = useState<Record<Stage, number>>({ ...DEFAULTS });
  const [errors, setErrors] = useState<Partial<Record<Stage, string>>>({});
  const [savedStages, setSavedStages] = useState<Set<Stage>>(new Set());

  // Seed from API once loaded.
  useEffect(() => {
    if (!thresholds.data) return;
    const updated = { ...DEFAULTS };
    for (const t of thresholds.data.thresholds) {
      if (STAGES.includes(t.stage_name as Stage)) {
        updated[t.stage_name as Stage] = t.threshold_days;
      }
    }
    setValues(updated);
  }, [thresholds.data]);

  const mutation = useMutation({
    mutationFn: (stage: Stage) =>
      upsertThreshold({
        integration_id: integrationID,
        stage_name: stage,
        threshold_days: values[stage],
      }),
    onSuccess: (_data, stage) => {
      qc.invalidateQueries({ queryKey: ['pipeline-rot', 'thresholds'] });
      setSavedStages((prev) => new Set([...prev, stage]));
      setTimeout(() => {
        setSavedStages((prev) => {
          const next = new Set(prev);
          next.delete(stage);
          return next;
        });
      }, 2000);
    },
  });

  const handleChange = (stage: Stage, raw: string) => {
    const v = parseInt(raw, 10);
    setValues((prev) => ({ ...prev, [stage]: isNaN(v) ? 0 : v }));
    setErrors((prev) => ({ ...prev, [stage]: validateDays(isNaN(v) ? 0 : v) ?? undefined }));
  };

  const handleSave = (stage: Stage) => {
    const err = validateDays(values[stage]);
    if (err) {
      setErrors((prev) => ({ ...prev, [stage]: err }));
      return;
    }
    mutation.mutate(stage);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Settings size={20} className="text-muted-foreground" />
        <div>
          <h1 className="text-lg font-semibold text-foreground">Rot Thresholds</h1>
          <p className="text-sm text-muted-foreground">
            Days a deal can stay in each pipeline stage before it is flagged as rotting.
            Range: {DAY_MIN}–{DAY_MAX} days.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Stage Thresholds</CardTitle>
          <CardDescription className="text-sm">
            Each stage is saved independently. Changes take effect on the next scan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {thresholds.isLoading && (
            <div className="space-y-4">
              {STAGES.map((s) => (
                <Skeleton key={s} className="h-14 w-full" />
              ))}
            </div>
          )}
          {thresholds.isError && (
            <div className="text-sm text-destructive flex items-center gap-2">
              <AlertCircle size={14} />
              Failed to load thresholds. {(thresholds.error as Error).message}
            </div>
          )}
          {(thresholds.data || thresholds.isError) && (
            <div className="space-y-4">
              {STAGES.map((stage) => {
                const errMsg = errors[stage];
                const saved = savedStages.has(stage);
                const saving = mutation.isPending && mutation.variables === stage;
                return (
                  <div key={stage} className="flex items-start gap-4">
                    <div className="w-28 flex-shrink-0">
                      <div className="text-sm font-medium text-foreground">{stage}</div>
                      <div className="text-xs text-muted-foreground">pipeline stage</div>
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={DAY_MIN}
                          max={DAY_MAX}
                          value={values[stage]}
                          onChange={(e) => handleChange(stage, e.target.value)}
                          className={`w-24 rounded-md border px-3 py-1.5 text-sm bg-background text-foreground ${
                            errMsg ? 'border-destructive' : 'border-border'
                          } focus:outline-none focus:ring-1 focus:ring-primary`}
                        />
                        <span className="text-sm text-muted-foreground">days</span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!!errMsg || saving}
                          onClick={() => handleSave(stage)}
                          className="gap-1.5"
                        >
                          {saved ? (
                            <>
                              <Check size={13} className="text-green-400" /> Saved
                            </>
                          ) : saving ? (
                            'Saving…'
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </div>
                      {errMsg && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle size={11} /> {errMsg}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
